import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { ADMIN_CONTENT_MAX_WIDTH } from "@/react/components/SettingsApp/SettingsShell/pageWidths";
import { PAGE_SURFACE } from "@/react/constants/pageSurface";

export interface StandardLayoutHeaderProps extends ComponentPropsWithoutRef<"div"> {
  appShellVariant?: boolean;
  filters?: ReactNode;
  hasBorder?: boolean;
  headline?: ReactNode;
  hideTitleRow?: boolean;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  /** Only respected when `appShellVariant` is true; ignored otherwise. */
  titleGutterClassName?: string;
}

export const StandardLayoutHeader = ({
  leftContent,
  rightContent,
  filters,
  headline,
  hasBorder = true,
  hideTitleRow = false,
  appShellVariant = false,
  titleGutterClassName = "px-4 lg:px-12",
  className,
  ...rest
}: StandardLayoutHeaderProps) => {
  const hasFilters = Boolean(filters);
  const hasHeadline = Boolean(headline);
  const { isViewOnlyMasquerading } = usePunditUserContext();

  if (appShellVariant) {
    return (
      <div {...rest} className={classNames("w-full", PAGE_SURFACE, className)}>
        {/* Gutter wraps the title row only; filters/headline render full-bleed
            so sections that already own their gutter don't double-pad. */}
        {!hideTitleRow && (
          <div className={titleGutterClassName}>
            <div
              className={classNames("mx-auto", ADMIN_CONTENT_MAX_WIDTH, {
                "border-b border-primary": hasBorder,
              })}
            >
              <div
                className={classNames(
                  "flex items-center justify-between gap-4 py-3 lg:pb-6 lg:pt-8",
                  {
                    "!cursor-not-allowed": isViewOnlyMasquerading,
                  },
                )}
                data-testid="space-header"
              >
                <div
                  className={classNames("min-w-0 flex-1", {
                    "!pointer-events-none": isViewOnlyMasquerading,
                  })}
                  data-testid="space-settings"
                >
                  {leftContent}
                </div>
                {rightContent && (
                  <div
                    className={classNames("relative flex shrink-0", {
                      "!pointer-events-none": isViewOnlyMasquerading,
                    })}
                  >
                    {rightContent}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {hasFilters && filters}
        {hasHeadline && headline}
      </div>
    );
  }

  return (
    <div
      {...rest}
      className={classNames(
        "w-full lg:sticky lg:top-0 lg:z-10",
        PAGE_SURFACE,
        className,
      )}
    >
      {!hideTitleRow && (
        <div
          className={classNames("flex h-14 items-center gap-4 px-6", {
            "border-b border-primary": hasBorder,
            "!cursor-not-allowed": isViewOnlyMasquerading,
          })}
          data-testid="space-header"
        >
          <div
            className={classNames("flex-1 truncate", {
              "!pointer-events-none": isViewOnlyMasquerading,
            })}
            data-testid="space-settings"
          >
            {leftContent}
          </div>
          <div
            className={classNames("relative flex", {
              "!pointer-events-none": isViewOnlyMasquerading,
            })}
          >
            {rightContent}
          </div>
        </div>
      )}
      {hasFilters && filters}
      {hasHeadline && headline}
    </div>
  );
};
